<template>
  <form class="col-12 b-form b-dialog__form">
    <div class="mb-4" v-if="error">
      <server-error :error="error"></server-error>
    </div>
    <div v-if="showModal">
      <div class="row mt-5 b-form__row_type_flex-end">
        <div class="col-4">
          <label>{{ $t('CONTENT.discountRates.rate') }}:</label>
          <numeric
            v-model="rate"
            class="b-numeric"
            center
            controls
            :min="0"
            :max="100"
          />
        </div>
        <div class="col-4">
          <label>
            {{ $t('CONTENT.date') }}:
          </label>
          <datepicker
            v-model="date"
            class="b-datepicker"
            :formatter="datepickerFormat"
          />
        </div>
      </div>
      <div class="row mt-5 b-form__row_type_flex-end">
        <div class="col-4">
          <label>
            {{ $t('CONTENT.assets.base_asset_type') }}:
          </label>
          <cool-select
            v-model="underlyingAssetTypeIdModel"
            :list="assetTypesForDiscountRate"
            option-value="idInDb"
            option-text="name"
            :placeholder="$t('CONTENT.choose')"
            @change="selectAssetType($event)"
          />
        </div>
        <div class="col-4">
          <label>{{ $tc('CONTENT.currency.currency_code', 1) }}:</label>
          <cool-select
            v-model="currentCurrencyCode"
            :list="allCurrencyCode"
            option-value="code"
            option-text="name"
            :placeholder="$t('CONTENT.choose')"
          />
        </div>
      </div>
      <div class="row mt-5 b-form__row_type_flex-end">
        <div class="col-4">
          <label>{{ $t('CONTENT.discountRates.rent_period_in_months') }}:</label>
          <numeric
            v-model="monthCountFrom"
            class="b-numeric"
            center
            controls
            :min="0"
            :max="maxValueForMonthCountFrom"
            @keypress="changeNumericValue($event)"
          />
        </div>
        <div class="col-4">
          <numeric
            v-model="monthCountTo"
            class="b-numeric"
            center
            controls
            :min="minValueForMonthCountTo"
            @keypress="changeNumericValue($event)"
          />
        </div>
      </div>
      <div class="row mt-5 b-form__row_type_flex-end">
        <div class="col-4">
          <label>{{ $t('CONTENT.discountRates.rent_sum') }}:</label>
          <numeric
            v-model="rentalAmountFrom"
            class="b-numeric"
            center
            controls
            :min="0"
            :max="maxValueForRentalAmountFrom"
          />
        </div>
        <div class="col-4">
          <numeric
            v-model="rentalAmountTo"
            class="b-numeric"
            center
            controls
            :min="minValueForRentalAmountTo"
          />
        </div>
      </div>
      <div class="row mt-5">
        <div class="col-6 offset-6">
          <div
            class="b-form__container b-form__container_type_flex b-form__container_type_flex_end"
          >
            <button
              data-cy="save-create-asset"
              class="b-btn b-btn_type_primary"
              type="button"
              @click="save"
            >
              {{ $t('CONTENT.save') }}
            </button>
            <button
              data-cy="cancel-create-asset"
              type="text"
              class="b-btn b-btn_type_empty m-2"
              @click.prevent="cancel"
            >
              {{ $t('CONTENT.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showNewDiscountRateNotification"
      class="mt-2"
    >
      <new-discount-rate-notification
        :save-contracts="saveContracts"
        :contracts-for-recalculation-download-excel="contractsForRecalculationDownloadExcel"
        :contracts-with-mixed-discount-rate-types-download-excel="contractsWithMixedDiscountRateTypesDownloadExcel"
        :contains-contracts-with-mixed-discount-rates="contractsWithMixedDiscountRateTypes.length>0"
        :cancel-save="cancelSave"
      />
    </div>
  </form>
</template>

<script>
import _ from 'lodash';
import datepickerFormat from '@/mixins/datepickerFormat.mixin';
import DiscountRateAPI from '@/plugins/discountRates/api/DiscountRateAPI';
import NewDiscountRateNotification from '@/components/NewDiscountRateNotification.vue';
import DiscountRate from '../../models/DiscountRate';
import dateConverter from '../../../../utils/converters/date.converter';
import changeNumericValue from '../../../../mixins/changeNumericValue';
import downloadFileMixin from '../../../../mixins/downloadFile';

export default {
  components: {NewDiscountRateNotification},
  mixins: [changeNumericValue, datepickerFormat, downloadFileMixin],
  props: {
    currentDiscountRate: {
      type: Object | null,
      required: true,
    },
    assetTypes: {
      type: Array,
      required: true,
    },
    currencyCodes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      error: null,
      form: {},
      assetTypesForDiscountRate: [],
      minValueForMonthCountTo: 1000000000,
      maxValueForMonthCountFrom: 1000000000,
      minValueForRentalAmountTo: 1000000000,
      maxValueForRentalAmountFrom: 1000000000,
      importContractsWithNewDiscountRates: [],
      contractsWithMixedDiscountRateTypes: [],
      contractsWithNewDiscountRates: null,
      showNewDiscountRateNotification: false,
      showModal: true,
      currencyCode: null,
    };
  },
  computed: {
    allCurrencyCode() {
      return this.currencyCodes;
    },
    underlyingAssetTypeIdModel: {
      get() {
        if (this.form.underlyingAssetTypeId === null) {
          return 123;
        }
        return this.form.underlyingAssetTypeId;
      },
      set(newVal) {
        if (newVal === 123) {
          this.form.underlyingAssetTypeId = null;
        } else {
          this.form.underlyingAssetTypeId = newVal;
        }
      },
    },
    date: {
      get() {
        if (this.form.date !== null) {
          const convertedStringDate = decodeURIComponent(this.form.date);
          return dateConverter.convertToDate(convertedStringDate);
        }

        return null;
      },
      set(newVal) {
        if (newVal !== null) {
          this.form.date = dateConverter.convertToString(newVal);
        } else {
          this.form.date = null;
        }
      },
    },
    rate: {
      get() {
        if (this.form.value !== null) {
          return this.form.value;
        }
      },
      set(newVal) {
        this.form.value = newVal;
      },
    },
    currentCurrencyCode: {
      get() {
        if (this.form.currencyCode !== null) {
          return this.form.currencyCode.toLowerCase();
        }
        return null;
      },
      set(newVal) {
        this.form.currencyCode = newVal.toUpperCase();
        return this.form.currencyCode;
      },
    },
    monthCountFrom: {
      get() {
        if (this.form.monthsCountFrom !== null) {
          return this.form.monthsCountFrom;
        }
      },
      set(newVal) {
        this.form.monthsCountFrom = newVal;
        this.minValueForMonthCountTo = newVal + 1;
      },
    },
    monthCountTo: {
      get() {
        if (this.form.monthsCountTo !== null) {
          return this.form.monthsCountTo;
        }
      },
      set(newVal) {
        this.form.monthsCountTo = newVal;
        this.maxValueForMonthCountFrom = newVal - 1;
      },
    },
    rentalAmountFrom: {
      get() {
        if (this.form.rentalAmountFrom !== null) {
          return this.form.rentalAmountFrom;
        }
      },
      set(newVal) {
        this.form.rentalAmountFrom = newVal;
        this.minValueForRentalAmountTo = newVal + 1;
      },
    },
    rentalAmountTo: {
      get() {
        if (this.form.rentalAmountTo !== null) {
          return this.form.rentalAmountTo;
        }
      },
      set(newVal) {
        this.form.rentalAmountTo = newVal;
        this.maxValueForRentalAmountFrom = newVal - 1;
      },
    },
  },
  watch: {
    monthCountFrom(newVal) {
      if (this.monthCountTo == null) {
        this.minValueForMonthCountTo = newVal + 1;
        this.maxValueForMonthCountFrom = newVal + 1;
      }
    },
    rentalAmountFrom(newVal) {
      if (this.rentalAmountTo == null) {
        this.minValueForRentalAmountTo = newVal + 1;
        this.maxValueForRentalAmountFrom = newVal + 1;
      }
    },
  },
  created() {
    if (this.currentDiscountRate) {
      this.form = _.clone(this.currentDiscountRate);
      if (this.form.monthCountTo !== null) {
        this.maxValueForMonthCountFrom = this.form.monthCountTo + 1;
      }

      if (this.form.monthCountFrom !== null) {
        this.minValueForMonthCountTo = this.form.monthCountFrom - 1;
      }

      if (this.form.rentalAmountTo !== null) {
        this.maxValueForRentalAmountFrom = this.form.rentalAmountTo + 1;
      }

      if (this.form.rentalAmountFrom !== null) {
        this.minValueForRentalAmountTo = this.form.rentalAmountFrom - 1;
      }
    } else {
      this.form = DiscountRate.createNew();
    }

    this.assetTypesForDiscountRate = [...this.assetTypes];
    this.assetTypesForDiscountRate.push({ idInDb: 123, name: 'Все' });
  },
  methods: {
    selectAssetType(value) {
      this.form.underlyingAssetTypeId = +value;
    },
    async fetchData() {
      try {
        this.contractsWithNewDiscountRates = await new DiscountRateAPI().validateDiscountRate(this.form);
        this.importContractsWithNewDiscountRates = this.contractsWithNewDiscountRates.contracts || [];
        this.contractsWithMixedDiscountRateTypes = this.contractsWithNewDiscountRates.contractsWithMixedDiscountRateTypes || [];
      } catch (e) {
        this.error = e;
      }
    },
    async save(event) {
      this.$fileLoadingManager.startLoading();
      try {
        await this.fetchData();
        this.showModal = !(this.importContractsWithNewDiscountRates.length > 0);
        this.showNewDiscountRateNotification = this.importContractsWithNewDiscountRates.length > 0;
        if (this.importContractsWithNewDiscountRates.length === 0) {
          event.preventDefault();
          this.$emit('save', this.form);
        }
      } catch (error) {
        this.error = error;
      } finally {
        this.$fileLoadingManager.finishLoading();
      }
    },
    async saveContracts() {
      this.$fileLoadingManager.startLoading();
      try {
        await this.fetchData();
        if (Array.isArray(this.contractsWithMixedDiscountRateTypes) && this.contractsWithMixedDiscountRateTypes.length > 0) {
          throw new Error(`Контракт с идентификатором ${this.contractsWithMixedDiscountRateTypes[0].id} содержит разные типы ставок дисконтирования`);
        }
        this.showNewDiscountRateNotification = false;
        this.$emit('save', this.form);
        this.$emit('cancel');
      } catch (e) {
        this.error = e;
      } finally {
        this.$fileLoadingManager.finishLoading();
      }
    },
    cancelSave() {
      this.showNewDiscountRateNotification = false;
      this.showModal = true;
    },
    async contractsForRecalculationDownloadExcel() {
      const originalType = this.importType;
      this.importType = 'editContractWithNewDiscountRate';
      await this.downloadTemplate();
      this.importType = originalType;
    },
    async contractsWithMixedDiscountRateTypesDownloadExcel() {
      const originalType = this.importType;
      this.importType = 'contractsWithMixedDiscountRateTypes';
      await this.downloadTemplate();
      this.importType = originalType;
    },
    cancel() {
      this.form = {};
      this.$emit('cancel');
    },
  },
};
</script>
